import React, { useState } from "react";
import { Loader2, Mail, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Subscribe-for-weekly-GA4-tips lead capture.
 *
 * Lightweight email-only form. Renders inline state for submitting,
 * success, "already subscribed", and validation errors. Designed to slot
 * into Blog index and BlogPost without imposing a card style — pass any
 * variant to flip the surface from light to dark.
 */
export function BlogSubscribeForm({ source = "blog-index", variant = "light" }) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | already | error
  const [errorMsg, setErrorMsg] = useState("");

  const dark = variant === "dark";

  async function handleSubmit(e) {
    e.preventDefault();
    if (status === "loading") return;
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setStatus("error");
      setErrorMsg("Please enter a valid email address.");
      return;
    }
    setStatus("loading");
    setErrorMsg("");
    try {
      const res = await api.post("/blog/subscribe", { email: trimmed, source });
      setStatus(res.data?.already_subscribed ? "already" : "success");
      setEmail("");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Try again in a moment.";
      setStatus("error");
      setErrorMsg(detail);
    }
  }

  if (status === "success" || status === "already") {
    return (
      <div
        className={`rounded-3xl p-6 md:p-8 flex items-start gap-3 ${
          dark ? "bg-[#85CDCB]/15 text-[#F7F5F0] border border-[#85CDCB]/40" : "bg-[#85CDCB]/15 text-[#1E392A] border border-[#85CDCB]/40"
        }`}
        data-testid="blog-subscribe-success"
      >
        <CheckCircle2 className="w-6 h-6 text-[#1E392A] shrink-0 mt-0.5" />
        <div>
          <div className="font-display text-xl leading-tight">
            {status === "already" ? "You're already on the list." : "You're in. Check your inbox."}
          </div>
          <p className={`text-sm mt-1.5 leading-relaxed ${dark ? "text-[#F7F5F0]/80" : "text-[#1E392A]/75"}`}>
            {status === "already"
              ? "We already have this email subscribed — the next weekly tip will land in your inbox automatically."
              : "We just sent a welcome note. Expect one short, practical GA4 tip every week."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={`rounded-3xl p-6 md:p-8 ${
        dark ? "bg-[#1E392A] text-[#F7F5F0]" : "bg-white border border-black/5 bento-shadow"
      }`}
      data-testid="blog-subscribe-form"
      noValidate
    >
      <div className={`tag-label ${dark ? "text-[#E27D60]" : "text-[#E27D60]"}`}>Weekly GA4 tips</div>
      <h3 className={`font-display text-2xl md:text-3xl mt-2 leading-tight ${dark ? "text-[#F7F5F0]" : "text-[#1E392A]"}`}>
        One short, practical GA4 tip in your inbox each week.
      </h3>
      <p className={`mt-2 text-sm leading-relaxed ${dark ? "text-[#F7F5F0]/75" : "text-[#1E392A]/70"}`}>
        No fluff. No jargon. The kind of tip that actually changes a number on your dashboard.
        Unsubscribe any time.
      </p>

      <div className="mt-5 flex flex-col sm:flex-row gap-3">
        <label className="flex-1 relative">
          <span className="sr-only">Email address</span>
          <Mail
            className={`w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 ${
              dark ? "text-[#F7F5F0]/55" : "text-[#1E392A]/45"
            }`}
            aria-hidden="true"
          />
          <input
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="you@yourcompany.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (status === "error") setStatus("idle");
            }}
            data-testid="blog-subscribe-email-input"
            className={`w-full h-12 rounded-full pl-11 pr-4 text-base outline-none transition-colors ${
              dark
                ? "bg-[#F7F5F0]/10 text-[#F7F5F0] placeholder:text-[#F7F5F0]/40 focus:bg-[#F7F5F0]/15"
                : "bg-[#F7F5F0] text-[#1E392A] placeholder:text-[#1E392A]/40 focus:bg-white border border-black/5 focus:border-[#E27D60]/50"
            }`}
            aria-invalid={status === "error"}
            aria-describedby={status === "error" ? "blog-subscribe-error" : undefined}
          />
        </label>
        <button
          type="submit"
          disabled={status === "loading"}
          data-testid="blog-subscribe-submit-button"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-[#E27D60] hover:bg-[#cf6c50] disabled:opacity-70 text-white font-medium px-6 h-12 shrink-0 transition-colors"
        >
          {status === "loading" ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> Subscribing…
            </>
          ) : (
            "Subscribe"
          )}
        </button>
      </div>

      {status === "error" && (
        <p
          id="blog-subscribe-error"
          data-testid="blog-subscribe-error"
          className="mt-3 text-sm text-[#E27D60]"
          role="alert"
        >
          {errorMsg}
        </p>
      )}
    </form>
  );
}
